// @ts-check
// Regras puras da tela Configurações > Modelos do WhatsApp e dos atalhos "Criar modelo" (contrato 08 de leads e
// WhatsApp, seção 7): grupos de situação, filtro e busca, resumo de Canais, motivo da recusa, destaque das variáveis
// e os endereços da tela. Desde a 2.0.0 (contrato 09 de campanhas de WhatsApp, seção 2) também o filtro de categoria,
// os selos de qualidade e de categoria, a junção das cópias da mesma conta e a aba WhatsApp de Campanhas > Modelos.
import { normalizeSearchText } from './utils.js';
import { readReturnPath as readSafeReturnPath } from './return-path.js';

export const WHATSAPP_TEMPLATES_PATH = '/settings/whatsapp-templates';
/** Aba WhatsApp de Campanhas > Modelos e as páginas do modelo de campanha (base, seção 2.5). */
export const CAMPAIGN_WHATSAPP_TEMPLATES_PATH = '/campaigns/templates?tab=whatsapp';
export const CAMPAIGN_WHATSAPP_TEMPLATE_BASE_PATH = '/campaigns/templates/whatsapp';

/** Chips de categoria (Configurações > Modelos do WhatsApp), na URL como ?category=. */
export const TEMPLATE_CATEGORY_FILTERS = Object.freeze(['ALL', 'MARKETING', 'UTILITY', 'AUTHENTICATION']);

// a mudança feita pela Meta fica em destaque por 30 dias
const RECENT_CATEGORY_CHANGE_MILLISECONDS = 30 * 24 * 60 * 60 * 1000;
const QUALITY_TONES = /** @type {Record<string, string>} */ (Object.freeze({ GREEN: 'green', YELLOW: 'yellow', RED: 'red', UNKNOWN: 'gray' }));

// situações da Meta agrupadas como a escola lê: "em análise" junta a revisão e o recurso
export const TEMPLATE_STATUS_GROUPS = Object.freeze({
  APPROVED: Object.freeze(['APPROVED']),
  IN_REVIEW: Object.freeze(['PENDING', 'IN_APPEAL']),
  REJECTED: Object.freeze(['REJECTED']),
  PAUSED: Object.freeze(['PAUSED']),
  DISABLED: Object.freeze(['DISABLED']),
  OTHER: Object.freeze(['LIMIT_EXCEEDED', 'PENDING_DELETION']),
});
export const TEMPLATE_STATUS_GROUP_KEYS = Object.freeze(/** @type {Array<keyof typeof TEMPLATE_STATUS_GROUPS>} */ (Object.keys(TEMPLATE_STATUS_GROUPS)));

// motivos de recusa que a Meta informa e têm texto próprio; os demais mostram o código
const KNOWN_REJECTED_REASONS = new Set(['INVALID_FORMAT', 'ABUSIVE_CONTENT', 'INCORRECT_CATEGORY', 'TAG_CONTENT_MISMATCH', 'PROMOTIONAL', 'SCAM', 'NONE']);

/**
 * @typedef {{ id: string, channelId: string, name: string, language: string, category: string | null, status: string, rejectedReason?: string | null,
 *   headerText?: string | null, bodyText?: string | null, footerText?: string | null, syncedAt?: string | null, updatedAt?: string | null,
 *   wabaId?: string | null, usage?: string | null, group?: string, qualityScore?: string | null, previousCategory?: string | null,
 *   categoryChangedAt?: string | null, correctCategory?: string | null, categoryChangeAt?: string | null, hasOptOutButton?: boolean,
 *   useCount?: number }} TemplateLike
 */

/** @param {string} status @returns {keyof typeof TEMPLATE_STATUS_GROUPS} */
export function statusGroupOf(status) {
  for (const key of TEMPLATE_STATUS_GROUP_KEYS) if (TEMPLATE_STATUS_GROUPS[key].includes(status)) return key;
  return 'OTHER';
}

/**
 * Filtro da tela: busca sem acento no nome, cabeçalho, corpo e rodapé; grupo de situação; número; categoria da Meta.
 * @template {TemplateLike} T
 * @param {T[]} templates @param {{ search?: string, statusGroup?: string | null, channelId?: string | null, category?: string | null }} filters
 * @returns {T[]}
 */
export function filterTemplates(templates, filters = {}) {
  const search = normalizeSearchText(filters.search ?? '');
  return templates.filter((template) => {
    if (filters.statusGroup && filters.statusGroup !== 'ALL' && statusGroupOf(template.status) !== filters.statusGroup) return false;
    if (filters.channelId && template.channelId !== filters.channelId) return false;
    if (filters.category && filters.category !== 'ALL' && (template.category ?? '') !== filters.category) return false;
    if (!search) return true;
    const haystack = normalizeSearchText([template.name, template.name.replaceAll('_', ' '), template.headerText, template.bodyText, template.footerText].filter(Boolean).join(' '));
    return haystack.includes(search);
  });
}

/**
 * Contagem por grupo (os chips) e a última atualização da Meta, para a tela e para o resumo em Canais.
 * @param {TemplateLike[]} templates
 */
export function summarizeTemplates(templates) {
  /** @type {Record<string, number>} */
  const counts = { ALL: templates.length };
  for (const key of TEMPLATE_STATUS_GROUP_KEYS) counts[key] = 0;
  /** @type {Record<string, number>} */
  const byCategory = { ALL: templates.length, MARKETING: 0, UTILITY: 0, AUTHENTICATION: 0 };
  let lastUpdatedAt = /** @type {string | null} */ (null);
  for (const template of templates) {
    counts[statusGroupOf(template.status)] += 1;
    if (template.category && template.category in byCategory) byCategory[template.category] += 1;
    const updatedAt = template.syncedAt ?? template.updatedAt ?? null;
    if (updatedAt && (!lastUpdatedAt || updatedAt > lastUpdatedAt)) lastUpdatedAt = updatedAt;
  }
  return {
    counts, byCategory, total: templates.length, approved: counts.APPROVED, inReview: counts.IN_REVIEW, rejected: counts.REJECTED, paused: counts.PAUSED,
    disabled: counts.DISABLED, lastUpdatedAt,
  };
}

/**
 * O que a tela de modelos mostra: carregando, a falha (com "Tentar de novo") antes de qualquer estado vazio, o aviso
 * de conectar um número ou a lista. Uma falha nunca vira "nenhum número" ou "nenhum modelo".
 * @param {{ channels: unknown[] | null, templates: unknown[] | null, loadFailed: boolean }} input
 * @returns {'LOADING' | 'LOAD_FAILED' | 'NO_CHANNEL' | 'READY'}
 */
export function templatesPageState({ channels, templates, loadFailed }) {
  if (channels === null || templates === null) return loadFailed ? 'LOAD_FAILED' : 'LOADING';
  if (channels.length === 0) return 'NO_CHANNEL';
  return 'READY';
}

/**
 * Partes do resumo de Canais: aprovados, em análise e recusados sempre; pausados e desativados só quando há, porque
 * deixam de sair nas conversas e nas automações. Recusados, pausados e desativados ficam em destaque quando há.
 * @param {ReturnType<typeof summarizeTemplates>} summary
 * @returns {Array<{ key: 'approved' | 'inReview' | 'rejected' | 'paused' | 'disabled', count: number, isHighlighted: boolean }>}
 */
export function templateSummaryParts(summary) {
  /** @type {Array<{ key: 'approved' | 'inReview' | 'rejected' | 'paused' | 'disabled', count: number, isHighlighted: boolean }>} */
  const parts = [
    { key: 'approved', count: summary.approved, isHighlighted: false },
    { key: 'inReview', count: summary.inReview, isHighlighted: false },
    { key: 'rejected', count: summary.rejected, isHighlighted: summary.rejected > 0 },
  ];
  if (summary.paused > 0) parts.push({ key: 'paused', count: summary.paused, isHighlighted: true });
  if (summary.disabled > 0) parts.push({ key: 'disabled', count: summary.disabled, isHighlighted: true });
  return parts;
}

/**
 * Chave do texto do motivo da recusa; código desconhecido cai no texto genérico com o código.
 * @param {string | null | undefined} code
 */
export function rejectedReasonKey(code) {
  const normalized = String(code || 'NONE').toUpperCase();
  return KNOWN_REJECTED_REASONS.has(normalized) ? `whatsappTemplates.rejectedReasons.${normalized}` : 'whatsappTemplates.rejectedReasons.OTHER';
}

/**
 * Como o cartão mostra a recusa: o texto e, só para um motivo conhecido, o código da Meta à parte (para o suporte). O
 * desconhecido já leva o código no texto, e o NONE não tem código para mostrar.
 * @param {string | null | undefined} code
 * @returns {{ key: string, code: string, showCode: boolean }}
 */
export function rejectedReasonDisplay(code) {
  const normalized = String(code || 'NONE').toUpperCase();
  const key = rejectedReasonKey(normalized);
  return { key, code: normalized, showCode: normalized !== 'NONE' && key !== 'whatsappTemplates.rejectedReasons.OTHER' };
}

/**
 * Partes do texto com as variáveis ({{1}} ou {{nome}}) separadas, para a prévia destacar cada uma.
 * @param {string | null | undefined} text
 * @returns {Array<{ kind: 'text' | 'variable', value: string }>}
 */
export function splitVariableSegments(text) {
  const source = String(text ?? '');
  /** @type {Array<{ kind: 'text' | 'variable', value: string }>} */
  const segments = [];
  let cursor = 0;
  for (const match of source.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
    const index = match.index ?? 0;
    if (index > cursor) segments.push({ kind: 'text', value: source.slice(cursor, index) });
    segments.push({ kind: 'variable', value: match[1] });
    cursor = index + match[0].length;
  }
  if (cursor < source.length) segments.push({ kind: 'text', value: source.slice(cursor) });
  return segments;
}

/**
 * Tela de modelos já no formulário de criação, com o número pré-escolhido quando há e a tela de origem (para o
 * "Voltar para onde você estava" depois de criar).
 * @param {string | null} [channelId] @param {string | null} [returnPath]
 */
export function buildCreateTemplateHref(channelId, returnPath = null) {
  const parameters = new URLSearchParams({ new: '1' });
  if (channelId) parameters.set('channelId', channelId);
  const from = readReturnPath(returnPath);
  if (from) parameters.set('from', from);
  return `${WHATSAPP_TEMPLATES_PATH}?${parameters}`;
}

/**
 * Caminho de volta aceito só dentro do CRM e fora da própria tela de modelos. A regra mora em return-path.js
 * (spec-2.0.0, K20); esta função continua aqui para quem já a importava.
 * @param {unknown} value @returns {string | null}
 */
export function readReturnPath(value) {
  return readSafeReturnPath(value, WHATSAPP_TEMPLATES_PATH);
}

/**
 * Variáveis do modelo na ordem do envio (cabeçalho e corpo, sem repetir).
 * @param {{ headerVariables?: string[], bodyVariables?: string[] } | null | undefined} template
 */
export function templateVariables(template) {
  if (!template) return [];
  return [...new Set([...(template.headerVariables ?? []), ...(template.bodyVariables ?? [])])];
}

/**
 * Modelo pronto para enviar: escolhido e com todas as variáveis preenchidas.
 * @param {{ headerVariables?: string[], bodyVariables?: string[] } | null | undefined} template @param {Record<string, string>} values
 */
export function isTemplateReady(template, values) {
  return Boolean(template) && templateVariables(template).every((variable) => String(values[variable] ?? '').trim() !== '');
}

/**
 * Sugestões para as variáveis mais comuns: o primeiro nome da família ({{1}}, nome, responsavel) e o do consultor
 * ({{2}}, consultor). Quem envia sempre pode trocar.
 * @param {{ headerVariables?: string[], bodyVariables?: string[] }} template @param {{ contactName?: string | null, memberName?: string | null }} names
 */
export function suggestTemplateValues(template, names) {
  const contactName = String(names.contactName ?? '').trim().split(/\s+/)[0] ?? '';
  const memberName = String(names.memberName ?? '').trim();
  /** @type {Record<string, string>} */
  const values = {};
  templateVariables(template).forEach((variable, index) => {
    if (/^(1|nome|name|responsavel|first_name)$/i.test(variable) || (index === 0 && /^\d+$/.test(variable))) values[variable] = values[variable] ?? contactName;
    else if (/^(2|consultor|atendente|agent)$/i.test(variable)) values[variable] = values[variable] ?? memberName;
  });
  return values;
}

/** @param {Record<string, string>} values */
export function trimTemplateValues(values) {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value ?? '').trim()]));
}

/**
 * Número de um canal de WhatsApp como a escola reconhece: o exibido pela Meta ou o handle.
 * @param {{ handle?: string, displayName?: string | null, settings?: Record<string, any> } | null | undefined} channel
 */
export function channelLabel(channel) {
  if (!channel) return '';
  return String(channel.settings?.displayPhoneNumber || (channel.handle ? `+${channel.handle}` : '') || channel.displayName || '');
}

/** Criar, sincronizar e excluir modelos seguem a flag WORKSPACE do papel, como as rotas. @param {string[] | null | undefined} flags */
export function canManageTemplates(flags) {
  return Array.isArray(flags) && flags.includes('WORKSPACE');
}

/**
 * Modelos de campanha (2.0.0): WORKSPACE em qualquer um; WHATSAPP_CAMPAIGNS só nos de uso de campanha (o motor confere).
 * @param {string[] | null | undefined} flags
 */
export function canManageCampaignTemplates(flags) {
  return Array.isArray(flags) && (flags.includes('WORKSPACE') || flags.includes('WHATSAPP_CAMPAIGNS'));
}

/**
 * Um modelo por conta do WhatsApp Business: as cópias do mesmo nome e idioma nos números da mesma WABA viram um item
 * com a lista dos números ("Números: A, B"). Sem WABA, cada número fica com a sua cópia.
 * @template {TemplateLike} T
 * @param {T[]} templates
 * @returns {Array<{ template: T, copies: T[], channelIds: string[] }>}
 */
export function dedupeTemplatesByWaba(templates) {
  /** @type {Map<string, { template: T, copies: T[], channelIds: string[] }>} */
  const groups = new Map();
  for (const template of templates) {
    const key = `${template.wabaId ? `waba:${template.wabaId}` : `channel:${template.channelId}`}\u0000${template.name}\u0000${template.language}`;
    const group = groups.get(key);
    if (!group) {
      groups.set(key, { template, copies: [template], channelIds: [template.channelId] });
      continue;
    }
    group.copies.push(template);
    if (!group.channelIds.includes(template.channelId)) group.channelIds.push(template.channelId);
    // a cópia aprovada representa o grupo (a de um número novo pode estar só sincronizando)
    if (group.template.status !== 'APPROVED' && template.status === 'APPROVED') group.template = template;
  }
  return [...groups.values()];
}

/**
 * Selos do cartão (2.0.0): qualidade, mudança de categoria anunciada ou feita há pouco, botão "Parar de receber",
 * comunicado e uso em campanhas. `key` é a chave de texto em `whatsappTemplates`.
 * @param {TemplateLike} template @param {number} [now]
 * @returns {Array<{ key: string, tone: string, params?: Record<string, unknown> }>}
 */
export function templateBadges(template, now = Date.now()) {
  /** @type {Array<{ key: string, tone: string, params?: Record<string, unknown> }>} */
  const badges = [];
  if (template.qualityScore && QUALITY_TONES[template.qualityScore]) badges.push({ key: `quality.${template.qualityScore}`, tone: QUALITY_TONES[template.qualityScore] });
  if (template.correctCategory && template.correctCategory !== template.category) {
    badges.push({ key: template.categoryChangeAt ? 'category.willChange' : 'category.willChangeSoon', tone: 'orange', params: { category: template.correctCategory, date: template.categoryChangeAt } });
  }
  const changedAt = template.categoryChangedAt ? Date.parse(template.categoryChangedAt) : Number.NaN;
  if (template.previousCategory && Number.isFinite(changedAt) && now - changedAt <= RECENT_CATEGORY_CHANGE_MILLISECONDS) {
    badges.push({ key: 'category.changed', tone: 'orange', params: { previous: template.previousCategory, category: template.category, date: template.categoryChangedAt } });
  }
  if (template.hasOptOutButton) badges.push({ key: 'badges.optOutButton', tone: 'blue' });
  if (template.group === 'ANNOUNCEMENT') badges.push({ key: 'badges.announcement', tone: 'purple' });
  if ((template.useCount ?? 0) > 0) badges.push({ key: 'inUse', tone: 'gray', params: { count: template.useCount } });
  return badges;
}

/** @param {string} templateId */
export const campaignTemplateHref = (templateId) => `${CAMPAIGN_WHATSAPP_TEMPLATE_BASE_PATH}/${encodeURIComponent(templateId)}`;

/**
 * Página "Novo modelo" de campanha, com a tela de origem quando há (atalho do editor da campanha e do nó do fluxo).
 * @param {{ from?: string | null, channelId?: string | null, kind?: 'MARKETING' | 'ANNOUNCEMENT' | null }} [options]
 */
export function campaignTemplateNewHref(options = {}) {
  const parameters = new URLSearchParams();
  const from = readSafeReturnPath(options.from, `${CAMPAIGN_WHATSAPP_TEMPLATE_BASE_PATH}/new`);
  if (from) parameters.set('from', from);
  if (options.channelId) parameters.set('channelId', options.channelId);
  if (options.kind) parameters.set('kind', options.kind);
  const query = parameters.toString();
  return `${CAMPAIGN_WHATSAPP_TEMPLATE_BASE_PATH}/new${query ? `?${query}` : ''}`;
}

/**
 * Onde inserir a variável no texto: no cursor, separada por espaço só de uma palavra colada (antes de pontuação, não).
 * @param {string} text @param {number} start @param {number} end @param {string} token
 * @returns {{ text: string, cursor: number }}
 */
export function insertVariableToken(text, start, end, token) {
  const before = text.slice(0, start);
  const after = text.slice(end);
  const spacerBefore = /[\p{L}\p{N}}]$/u.test(before) ? ' ' : '';
  const spacerAfter = /^[\p{L}\p{N}{]/u.test(after) ? ' ' : '';
  const head = `${before}${spacerBefore}${token}`;
  return { text: `${head}${spacerAfter}${after}`, cursor: head.length };
}

/**
 * Limite diário em Canais (2.0.0): se é sem limite, quanto do limite foi usado (porcentagem) e se as campanhas estão sem vaga.
 * @param {{ limit: number | null, tier: string | null, usedLast24h: number, availableNow: number | null }} portfolio
 */
export function describeMessagingLimit(portfolio) {
  const isUnlimited = portfolio.limit === null && portfolio.tier === 'TIER_UNLIMITED';
  const usedShare = portfolio.limit ? Math.min(100, Math.round((portfolio.usedLast24h / portfolio.limit) * 100)) : 0;
  return { isUnlimited, usedShare, isFull: portfolio.availableNow === 0 };
}
